export type FracSimWorkflowStep =
  | 'DESIGN_BASIS'
  | 'ENGINEERING_CONFIG'
  | 'SIMULATION'
  | 'OPTIMIZATION'
  | 'COMPARISON'
  | 'FULL_WELL';

export type FracSimSubSection =
  | '基础设置'
  | '地质与力学'
  | '井筒与完井'
  | '泵注方案'
  | '高级物理'
  | '数值设置'
  | '结果分析'
  | '优化设计'
  | '敏感性分析';

export type ScenarioWorkflowStep =
  | 'DESIGN_BASIS'
  | 'SENSITIVITY'
  | 'OPTIMIZATION'
  | 'COMPARISON'
  | 'FULL_WELL';

export type WorkflowSubSection = {
  id: FracSimSubSection;
  label: string;
  workflowStep: FracSimWorkflowStep;
  panelKey: 'CONFIGURATION' | 'RESULTS' | 'SCENARIO';
  description?: string;
};

export const FRACSIM_WORKFLOW_STEPS: ReadonlyArray<{
  id: FracSimWorkflowStep;
  label: string;
}> = [
  { id: 'SIMULATION', label: '裂缝扩展模拟' },
  { id: 'OPTIMIZATION', label: '敏感性分析' },
  { id: 'COMPARISON', label: '方案对比' },
  { id: 'FULL_WELL', label: '全井复盘' },
];

export const workflowSections: Record<FracSimWorkflowStep, readonly WorkflowSubSection[]> = {
  DESIGN_BASIS: [
    { id: '优化设计', label: '设计依据', workflowStep: 'DESIGN_BASIS', panelKey: 'SCENARIO' },
  ],
  ENGINEERING_CONFIG: [
    { id: '基础设置', label: '基础设置', workflowStep: 'ENGINEERING_CONFIG', panelKey: 'CONFIGURATION' },
    { id: '地质与力学', label: '地质与力学', workflowStep: 'ENGINEERING_CONFIG', panelKey: 'CONFIGURATION' },
    { id: '井筒与完井', label: '井筒与完井', workflowStep: 'ENGINEERING_CONFIG', panelKey: 'CONFIGURATION' },
    { id: '泵注方案', label: '泵注方案', workflowStep: 'ENGINEERING_CONFIG', panelKey: 'CONFIGURATION' },
    { id: '高级物理', label: '天然裂缝', workflowStep: 'ENGINEERING_CONFIG', panelKey: 'CONFIGURATION' },
    { id: '数值设置', label: '数值设置', workflowStep: 'ENGINEERING_CONFIG', panelKey: 'CONFIGURATION' },
  ],
  SIMULATION: [
    { id: '结果分析', label: '结果分析', workflowStep: 'SIMULATION', panelKey: 'RESULTS' },
  ],
  OPTIMIZATION: [
    { id: '敏感性分析', label: '敏感性分析', workflowStep: 'OPTIMIZATION', panelKey: 'SCENARIO' },
  ],
  COMPARISON: [
    { id: '优化设计', label: '方案对比', workflowStep: 'COMPARISON', panelKey: 'SCENARIO' },
  ],
  FULL_WELL: [
    { id: '优化设计', label: '全井汇总', workflowStep: 'FULL_WELL', panelKey: 'SCENARIO' },
  ],
};

export const defaultSubSection = (workflowStep: FracSimWorkflowStep): FracSimSubSection =>
  workflowSections[workflowStep][0].id;

export const isWorkflowStep = (value: unknown): value is FracSimWorkflowStep =>
  FRACSIM_WORKFLOW_STEPS.some((step) => step.id === value);

export const isSubSectionForWorkflow = (
  workflowStep: FracSimWorkflowStep,
  subSection: unknown,
): subSection is FracSimSubSection =>
  workflowSections[workflowStep].some((section) => section.id === subSection);

export const normalizeFracSimNavigation = (
  workflowStep: unknown,
  subSection: unknown,
  legacyWorkflowStep: unknown = undefined,
): { activeWorkflowStep: FracSimWorkflowStep; activeSubSection: FracSimSubSection } => {
  let activeWorkflowStep: FracSimWorkflowStep;
  if (isWorkflowStep(workflowStep)) {
    activeWorkflowStep = workflowStep;
  } else if (workflowStep === 'DESIGN_BASIS' || workflowStep === 'ENGINEERING_CONFIG'
    || legacyWorkflowStep === 'DESIGN_BASIS') {
    activeWorkflowStep = 'SIMULATION';
  } else if (legacyWorkflowStep === 'SENSITIVITY' || legacyWorkflowStep === 'OPTIMIZATION') {
    activeWorkflowStep = 'OPTIMIZATION';
  } else if (legacyWorkflowStep === 'COMPARISON' || legacyWorkflowStep === 'FULL_WELL') {
    activeWorkflowStep = legacyWorkflowStep;
  } else {
    activeWorkflowStep = 'SIMULATION';
  }

  const legacySubSection = legacyWorkflowStep === 'SENSITIVITY' ? '敏感性分析' : subSection;
  return {
    activeWorkflowStep,
    activeSubSection: isSubSectionForWorkflow(activeWorkflowStep, legacySubSection)
      ? legacySubSection
      : defaultSubSection(activeWorkflowStep),
  };
};

export const scenarioWorkflowStepFor = (
  workflowStep: FracSimWorkflowStep,
  subSection: FracSimSubSection,
): ScenarioWorkflowStep => {
  if (workflowStep === 'OPTIMIZATION' && subSection === '敏感性分析') return 'SENSITIVITY';
  if (workflowStep === 'ENGINEERING_CONFIG' || workflowStep === 'SIMULATION') return 'DESIGN_BASIS';
  return workflowStep;
};
